import { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import styles from './courses.module.css';
import { useStores } from '../../hooks/useStore';
import { observer } from 'mobx-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../components/UI/Button/Button';
import { ReactComponent as EditIco } from './icon/edit.svg';
import { AppSettings } from '../../appSettings';
import { Course, CourseModule, Lesson } from '../../models/courses';
import CoursesPopUP from '../../components/popUp/Courses/Courses';
import CourseModules from './CourseModules';
import { ModuleLessons } from './ModuleLessons';
import LessonEditor from './LessonEditor';

type DrillLevel =
  | 'COURSE_LIST'
  | 'MODULE_LIST'
  | 'LESSON_LIST'
  | 'LESSON_EDITOR';

interface SortableCourseRowProps {
  course: Course;
  onEdit: (item: Course) => void;
  onSelect: (item: Course) => void;
}

const SortableCourseRow: React.FC<SortableCourseRowProps> = ({
  course,
  onEdit,
  onSelect,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: course.id });

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={rowStyle}
      className={styles.courseRow}
      onClick={() => onSelect(course)}
    >
      <div
        className={styles.dragHandle}
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
          <circle cx="5" cy="4" r="1.5" fill="#b4b8cf" />
          <circle cx="11" cy="4" r="1.5" fill="#b4b8cf" />
          <circle cx="5" cy="10" r="1.5" fill="#b4b8cf" />
          <circle cx="11" cy="10" r="1.5" fill="#b4b8cf" />
          <circle cx="5" cy="16" r="1.5" fill="#b4b8cf" />
          <circle cx="11" cy="16" r="1.5" fill="#b4b8cf" />
        </svg>
      </div>

      <img
        className={styles.courseRowImage}
        src={AppSettings.api.url + course.image}
        alt={course.title}
      />

      <div className={styles.courseRowInfo}>
        <span className={styles.courseRowTitle}>{course.title}</span>
        <span className={styles.courseRowUrl}>{course.url}</span>
        {course.description && (
          <span className={styles.courseRowDesc}>{course.description}</span>
        )}
      </div>

      <div className={styles.courseRowBadges}>
        <span
          className={
            course.is_published ? styles.badgePublished : styles.badgeDraft
          }
        >
          {course.is_published ? 'Опубликован' : 'Черновик'}
        </span>
      </div>

      <div className={styles.courseRowActions}>
        <EditIco
          className={styles.tagDeleteIco}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(course);
          }}
        />
      </div>
    </div>
  );
};

const Courses: React.FC = observer(() => {
  const api = useApi();
  const store = useStores();

  const [showForm, setShowForm] = useState<boolean>(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [course, setCourse] = useState<Course | null>(null);

  const [drillLevel, setDrillLevel] = useState<DrillLevel>('COURSE_LIST');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedModule, setSelectedModule] = useState<CourseModule | null>(
    null,
  );
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() => {
    loadCourses();
    // eslint-disable-next-line
  }, []);

  const loadCourses = async () => {
    try {
      store.notificationsStore.showPreloader();
      const { body } = await api.courses.get(0, 1000);
      if (body) {
        const sorted = [...(body as Course[])].sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
        );
        setCourses(sorted);
      }
    } catch {
      store.notificationsStore.setError('Ошибка при загрузке');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = courses.findIndex((c) => c.id === active.id);
    const newIndex = courses.findIndex((c) => c.id === over.id);
    const reordered = arrayMove(courses, oldIndex, newIndex);
    setCourses(reordered);

    try {
      const updates = reordered
        .map((c, index) => ({ course: c, newOrder: index }))
        .filter(({ course: c, newOrder }) => (c.sort_order ?? 0) !== newOrder);

      await Promise.all(
        updates.map(({ course: c, newOrder }) => {
          const fd = new FormData();
          fd.append('title', c.title);
          fd.append('url', c.url);
          if (c.description) {
            fd.append('description', c.description);
          }
          fd.append('sort_order', String(newOrder));
          fd.append('is_published', String(c.is_published ?? false));
          return api.courses.update(fd, c.id);
        }),
      );

      setCourses((prev) =>
        prev.map((c, index) => ({ ...c, sort_order: index })),
      );
    } catch {
      store.notificationsStore.setError('Ошибка при сохранении порядка');
      loadCourses();
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setCourse(null);
  };

  const update = () => {
    loadCourses();
  };

  const editItem = (item: Course) => {
    setCourse(item);
    setShowForm(true);
  };

  const handleSelectCourse = (item: Course) => {
    setSelectedCourse(item);
    setDrillLevel('MODULE_LIST');
  };

  const handleSelectModule = (module: CourseModule) => {
    setSelectedModule(module);
    setDrillLevel('LESSON_LIST');
  };

  const handleSelectLesson = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    setDrillLevel('LESSON_EDITOR');
  };

  const handleBackToCourselist = () => {
    setDrillLevel('COURSE_LIST');
    setSelectedCourse(null);
  };

  const handleBackToModules = () => {
    setDrillLevel('MODULE_LIST');
    setSelectedModule(null);
  };

  const handleBackToLessons = () => {
    setDrillLevel('LESSON_LIST');
    setSelectedLesson(null);
  };

  return (
    <>
      {showForm && (
        <CoursesPopUP onClose={closeForm} update={update} item={course} />
      )}
      <div className={styles.container}>
        {drillLevel === 'COURSE_LIST' && (
          <div className={styles.pageName}>
            <div className={styles.top}>
              <div className={styles.title}>
                <h2>Курсы</h2>
              </div>
              <Button
                style={{ maxWidth: 150 }}
                appearance="auth"
                onClick={() => setShowForm(showForm ? false : true)}
              >
                Добавить
              </Button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={courses.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className={styles.courseList}>
                  {courses.map((row) => (
                    <SortableCourseRow
                      key={row.id}
                      course={row}
                      onEdit={editItem}
                      onSelect={handleSelectCourse}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {drillLevel === 'MODULE_LIST' && selectedCourse && (
          <CourseModules
            course={selectedCourse}
            onSelectModule={handleSelectModule}
            onBack={handleBackToCourselist}
          />
        )}

        {drillLevel === 'LESSON_LIST' && selectedCourse && selectedModule && (
          <ModuleLessons
            course={selectedCourse}
            module={selectedModule}
            onSelectLesson={handleSelectLesson}
            onBack={handleBackToModules}
            onBackToCourses={handleBackToCourselist}
          />
        )}

        {drillLevel === 'LESSON_EDITOR' &&
          selectedCourse &&
          selectedModule &&
          selectedLesson && (
            <LessonEditor
              course={selectedCourse}
              module={selectedModule}
              lesson={selectedLesson}
              onBack={handleBackToLessons}
              onSaved={handleBackToLessons}
              onBackToCourses={handleBackToCourselist}
              onBackToModules={handleBackToModules}
            />
          )}
      </div>
    </>
  );
});

export default Courses;
